import "./SegmentationLabeling.scss";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import * as UserRedux from "../redux/user";
import * as R from "ramda";
import cn from "classnames";
import { Brand60 } from "../utils/colors";
import SegmentationLabelingTable, { SegmentRowEdit } from "./SegmentationLabelingTable";
import { CampaignRow } from "@blisspointmedia/bpm-types/dist/CampaignLabelingTool";
import { SegmentationMappingLabelRule } from "@blisspointmedia/bpm-types/dist/SegmentationMapping";
import {
  awaitJSON,
  CrossChannelLambdaFetch,
  MiscLambdaFetch,
  pollS3,
  SegmentationMappingLambdaFetch,
} from "../utils/fetch-utils";
import { useCompanyInfo } from "../redux/company";
import { useSetError } from "../redux/modals";
import { RouteComponentProps } from "@reach/router";
import { Option } from "../Components/FilterBar";
import {
  basicToAdvanced,
  Button,
  ButtonType,
  FilterBar,
  FullPageSpinner,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from "../Components";
import { useStateFunction } from "../utils/hooks/useData";
import PendingSegmentationChanges from "./PendingSegmentationChanges";
import { CustomSegmentsData } from "./SegmentationMapping";
import IncompleteUpdatesModal from "./IncompleteUpdatesModal";
import pako from "pako";
import { Nav } from "react-bootstrap";
import { MdDelete, MdErrorOutline, MdOutlineFileDownload, MdSave, MdSend } from "react-icons/md";
import { BiBookmarkHeart, BiPlusMedical } from "react-icons/bi";
import { ButtonFrameworkVariant } from "../Components/ButtonFramework";
import { Mixpanel, MxE } from "../utils/mixpanelWrapper";
import { formatNumber } from "../utils/format-utils";
import { downloadJSONToCSV } from "../utils/download-utils";
import SelectLabelingRulesModal, {
  ModifiedSegmentationMappingLabelRule,
} from "./SelectLabelingRulesModal";
import { makeFilter } from "../utils/boolean-logic-parser-creator";
import { db } from "../utils/db";
import AlertDialog, { AlertDialogVariants } from "../Components/AlertDialog/AlertDialog";

const CHUNK_SIZE = 250;

export interface SegmentRow extends CampaignRow {
  ad_group_id?: string;
  ad_group_name?: string;
  ad_id?: string;
  ad_name?: string;
  ingested_seg_id?: string;
  pg_seg_id?: string;
  Channel?: string;
  Platform?: string;
  channel_id?: number;
  platform_id?: number;
}

export interface LabelingDexieDBItem {
  cid: string;
  campaignKey: string;
  batchType: "labeled" | "unlabeled";
  rowData: SegmentRow;
  timestamp: number;
}

export interface SegmentEditsMap {
  [campaign_id: string]: SegmentRow;
}

interface SegmentationLabelingProps {
  data: any[] | null;
  customSegments: CustomSegmentsData[] | undefined;
  fetchRawData: () => void;
  dataGranularity: "ad" | "ad_group" | "campaign";
  labelingRules?: SegmentationMappingLabelRule[];
}

enum TabOptions {
  LABELED = "Labeled",
  UNLABELED = "Unlabeled",
}

interface UpdatedRow {
  campaign_id: string;
  ad_group_id?: string;
  ad_id?: string;
  segmentMap: Record<string, number>;
}

export interface EditsMapAction {
  type:
    | "UPDATE_SEGMENT_EDIT"
    | "DELETE_SEGMENT_EDIT"
    | "SET_STATE_FROM_CACHE"
    | "SET_MAP"
    | "RESET";
  cachedState?: SegmentEditsMap;
  editsMap?: SegmentEditsMap;
  updates?: { rows: SegmentRow[]; edits: SegmentRowEdit }[];
  campaign_keys?: string[];
  segmentName?: string;
}

const editsMapReducer = (state: SegmentEditsMap, action: EditsMapAction) => {
  const { type, cachedState, editsMap, updates = [], campaign_keys, segmentName } = action;

  switch (type) {
    case "UPDATE_SEGMENT_EDIT": {
      const newState = { ...state };

      for (const update of updates) {
        const { rows, edits } = update;
        if (!rows) {
          throw new Error("No row provided for updating segment edit operation.");
        }

        rows.forEach(row => {
          const existingRow = newState[row.campaign_key];

          if (!existingRow) {
            newState[row.campaign_key] = { ...row, edits: { ...edits } };
          } else {
            const updatedEdits = { ...existingRow.edits, ...edits };

            // Remove edits equivalent to initial values
            for (const segmentName of Object.keys(updatedEdits)) {
              const segmentEdit = updatedEdits[segmentName];
              if (segmentEdit?.label === row[segmentName]) {
                delete updatedEdits[segmentName];
              }
            }

            newState[row.campaign_key] = { ...existingRow, edits: updatedEdits };

            const rowEdits = R.pathOr({}, [row.campaign_key, "edits"], newState);

            // Remove the campaign_key if its edits become empty
            if (Object.keys(rowEdits).length === 0) {
              delete newState[row.campaign_key];
            }
          }
        });
      }

      return newState;
    }
    case "DELETE_SEGMENT_EDIT": {
      if (!campaign_keys) {
        throw new Error("No campaign_keys provided for deleting segment edit operation.");
      }

      if (!segmentName) {
        throw new Error("No segmentName provided for deleting segment edit operation.");
      }

      const newState = R.clone(state);

      campaign_keys.forEach(campaign_key => {
        const updatedRow = newState[campaign_key];
        const updatedRowEdits = updatedRow.edits ?? {};
        delete updatedRowEdits[segmentName];

        if (Object.keys(updatedRowEdits).length === 0) {
          delete newState[campaign_key];
        }
      });

      return newState;
    }
    case "SET_STATE_FROM_CACHE":
      return { ...cachedState, ...state };
    case "SET_MAP":
      return { ...editsMap };
    case "RESET":
      return {};
  }
};

const SegmentationLabeling = ({
  data,
  customSegments,
  fetchRawData,
  dataGranularity,
  labelingRules = [],
}: SegmentationLabelingProps & RouteComponentProps): JSX.Element => {
  const { cid } = useCompanyInfo();
  let userRole = useSelector(UserRedux.roleSelector);
  const setError = useSetError();

  const [selectedRows, setSelectedRows] = useState<Record<string, SegmentRow>>({});
  const [activeTab, setActiveTab] = useState<string>(TabOptions.UNLABELED);
  const [showIncompleteUpdatesModal, setShowIncompleteUpdatesModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [labeledEditsMap, setLabeledEditsMap] = useState<SegmentEditsMap>({});
  const [unlabeledEditsMap, setUnlabeledEditsMap] = useState<SegmentEditsMap>({});

  const [dialog, setDialog] = useState<{
    show: boolean;
    title: string;
    body: string;
    variant?: AlertDialogVariants;
  } | null>(null);

  const loadDataFromDexie = useCallback(async () => {
    try {
      const [labeledRecords, unlabeledRecords] = await Promise.all([
        db.editRecords
          .where("cid")
          .equals(cid)
          .and(record => record.batchType === "labeled")
          .toArray(),
        db.editRecords
          .where("cid")
          .equals(cid)
          .and(record => record.batchType === "unlabeled")
          .toArray(),
      ]);

      const labeledMap: SegmentEditsMap = {};
      const unlabeledMap: SegmentEditsMap = {};

      labeledRecords.forEach(record => {
        labeledMap[record.campaignKey] = record.rowData;
      });

      unlabeledRecords.forEach(record => {
        unlabeledMap[record.campaignKey] = record.rowData;
      });

      setLabeledEditsMap(labeledMap);
      setUnlabeledEditsMap(unlabeledMap);
    } catch (error) {
      console.error("Error loading data from Dexie:", error);
    }
  }, [cid]);

  useEffect(() => {
    if (!cid) {
      return;
    }

    loadDataFromDexie();
  }, [cid, loadDataFromDexie]);

  const updateEdits = useCallback(
    async (
      updates: { rows: SegmentRow[]; edits: SegmentRowEdit }[],
      batchType: "labeled" | "unlabeled"
    ) => {
      if (!cid) {
        return;
      }

      const currentEditsMap = batchType === "labeled" ? labeledEditsMap : unlabeledEditsMap;
      const setEditsMap = batchType === "labeled" ? setLabeledEditsMap : setUnlabeledEditsMap;
      const newEditsMap = { ...currentEditsMap };

      for (const update of updates) {
        const { rows, edits } = update;
        if (!rows) {
          throw new Error("No row provided for updating segment edit operation.");
        }

        rows.forEach(row => {
          const existingRow = newEditsMap[row.campaign_key];

          if (!existingRow) {
            newEditsMap[row.campaign_key] = { ...row, edits: { ...edits } };
          } else {
            const updatedEdits = { ...existingRow.edits, ...edits };

            for (const segmentName of Object.keys(updatedEdits)) {
              const segmentEdit = updatedEdits[segmentName];
              if (segmentEdit?.label === row[segmentName]) {
                delete updatedEdits[segmentName];
              }
            }

            newEditsMap[row.campaign_key] = { ...existingRow, edits: updatedEdits };

            const rowEdits = R.pathOr({}, [row.campaign_key, "edits"], newEditsMap);

            if (Object.keys(rowEdits).length === 0) {
              delete newEditsMap[row.campaign_key];
            }
          }
        });
      }

      setEditsMap(prevEditsMap => {
        return { ...prevEditsMap, ...newEditsMap };
      });
      for (const [campaignKey, rowData] of Object.entries({ ...currentEditsMap, ...newEditsMap })) {
        await db.editRecords.add({
          cid,
          campaignKey,
          batchType,
          rowData: rowData as SegmentRow,
          timestamp: Date.now(),
        });
      }
    },
    [cid, labeledEditsMap, unlabeledEditsMap]
  );

  // NOTE: there's some typing happening that makes replacing updateLabeledEdits/updateUnlabeledEdits functions with updateEdits(updates, batchType) more complicated, so leaving this for now.
  const updateLabeledEdits = useCallback(
    (updates: { rows: SegmentRow[]; edits: SegmentRowEdit }[]) => {
      return updateEdits(updates, "labeled");
    },
    [updateEdits]
  );

  const updateUnlabeledEdits = useCallback(
    (updates: { rows: SegmentRow[]; edits: SegmentRowEdit }[]) => {
      return updateEdits(updates, "unlabeled");
    },
    [updateEdits]
  );

  const completeUnlabeledEditsMap = useMemo(() => {
    if (!customSegments) {
      return {};
    }

    const unlabeledEditsMapCopy = R.clone(unlabeledEditsMap);
    const segmentKeys = Object.keys(unlabeledEditsMap);
    const segmentNames = customSegments.map(segment => segment.segmentName);

    for (const key of segmentKeys) {
      const value = unlabeledEditsMapCopy[key];

      let hasAllSegments = true;
      for (const segmentName of segmentNames) {
        hasAllSegments =
          hasAllSegments && value.edits && (value.edits[segmentName] ?? value[segmentName]);
      }

      if (!hasAllSegments) {
        delete unlabeledEditsMapCopy[key];
      }
    }

    return unlabeledEditsMapCopy;
  }, [customSegments, unlabeledEditsMap]);

  // NOTE: will edit once we allow for saving incomplete rows
  const removeCompleteUnlabeledEditsFromDexie = useCallback(async (): Promise<void> => {
    if (!cid || !completeUnlabeledEditsMap) {
      return;
    }

    try {
      const campaignKeysToDelete: string[] = (Object.values(
        completeUnlabeledEditsMap
      ) as SegmentRow[])
        .map((edit: SegmentRow) => edit.campaign_key)
        .filter((key): key is string => Boolean(key));

      if (campaignKeysToDelete.length === 0) {
        return;
      }

      await db.editRecords
        .where("cid")
        .equals(cid)
        .and(
          (record: LabelingDexieDBItem) =>
            record.batchType === "unlabeled" && campaignKeysToDelete.includes(record.campaignKey)
        )
        .delete();

      console.log(
        `Removed ${campaignKeysToDelete.length} complete unlabeled edits from DexieDB and local state`
      );
    } catch (error) {
      console.error("Error removing complete unlabeled edits from DexieDB:", error);
    }
  }, [cid, completeUnlabeledEditsMap]);

  const deleteSegmentEdit = useCallback(
    async (campaign_keys: string[], segmentName: string, batchType: "labeled" | "unlabeled") => {
      if (!cid || !campaign_keys || !segmentName) {
        return;
      }

      const currentEditsMap = batchType === "labeled" ? labeledEditsMap : unlabeledEditsMap;
      const newEditsMap = R.clone(currentEditsMap);

      campaign_keys.forEach(campaign_key => {
        const updatedRow = newEditsMap[campaign_key];
        const updatedRowEdits = updatedRow.edits ?? {};
        delete updatedRowEdits[segmentName];

        if (Object.keys(updatedRowEdits).length === 0) {
          delete newEditsMap[campaign_key];
        }
      });

      if (batchType === "labeled") {
        setLabeledEditsMap(newEditsMap);
      } else {
        setUnlabeledEditsMap(newEditsMap);
      }

      await db.editRecords
        .where("cid")
        .equals(cid)
        .and(record => record.batchType === batchType)
        .delete();

      const editRecords = Object.entries(newEditsMap).map(([campaignKey, rowData]) => ({
        cid,
        campaignKey,
        batchType,
        rowData: rowData as SegmentRow,
        timestamp: Date.now(),
      }));

      if (editRecords.length > 0) {
        await db.editRecords.bulkAdd(editRecords);
      }
    },
    [cid, labeledEditsMap, unlabeledEditsMap]
  );

  const resetEdits = useCallback(
    async (batchType: "labeled" | "unlabeled") => {
      if (!cid) {
        return;
      }

      if (batchType === "labeled") {
        setLabeledEditsMap({});
      } else {
        setUnlabeledEditsMap({});
      }

      await db.editRecords
        .where("cid")
        .equals(cid)
        .and(record => record.batchType === batchType)
        .delete();
    },
    [cid]
  );

  const filterBarOptions = useMemo(() => {
    const uniqueSegmentOptions =
      customSegments?.map(segment => ({
        name: segment.segmentName,
        label: segment.segmentName,
      })) ?? [];

    const options = [
      ...uniqueSegmentOptions,
      { name: "account_id", label: "Account ID" },
      { name: "account_name", label: "Account Name" },
      { name: "campaign_id", label: "Campaign ID" },
      { name: "campaign_name", label: "Campaign" },
    ];

    if (dataGranularity === "ad_group" || dataGranularity === "ad") {
      options.push({ name: "ad_group_id", label: "Ad Group ID" });
      options.push({ name: "ad_group_name", label: "Ad Group" });
    }

    if (dataGranularity === "ad") {
      options.push({ name: "ad_id", label: "Ad ID" });
      options.push({ name: "ad_name", label: "Ad" });
    }
    return options;
  }, [customSegments, dataGranularity]);

  const unlabeledData = useMemo(() => {
    return data?.filter(row => !(row.pg_seg_id || row.ingested_seg_id));
  }, [data]);

  const labeledData = useMemo(() => {
    return data?.filter(row => row.pg_seg_id || row.ingested_seg_id);
  }, [data]);

  const incompleteEditsMap = useMemo(() => {
    if (!customSegments) {
      return {};
    }

    const unlabeledEditsMapCopy = R.clone(unlabeledEditsMap);
    const segmentKeys = Object.keys(unlabeledEditsMap);
    const segmentNames = customSegments.map(segment => segment.segmentName);

    for (const key of segmentKeys) {
      const value = unlabeledEditsMapCopy[key];

      let hasAllSegments = true;
      for (const segmentName of segmentNames) {
        hasAllSegments =
          hasAllSegments && value.edits && (value.edits[segmentName] ?? value[segmentName]);
      }

      if (hasAllSegments) {
        delete unlabeledEditsMapCopy[key];
      }
    }

    return unlabeledEditsMapCopy;
  }, [customSegments, unlabeledEditsMap]);

  const dataToUse = useMemo(() => {
    return activeTab === TabOptions.UNLABELED
      ? {
          data: unlabeledData,
          editsMap: unlabeledEditsMap,
          updateEdits: updateUnlabeledEdits,
        }
      : { data: labeledData, editsMap: labeledEditsMap, updateEdits: updateLabeledEdits };
  }, [
    labeledData,
    labeledEditsMap,
    activeTab,
    unlabeledData,
    unlabeledEditsMap,
    updateLabeledEdits,
    updateUnlabeledEdits,
  ]);

  const [filter, setFilter] = useStateFunction<(line) => boolean>(() => true);

  const filteredData = useMemo(() => {
    return dataToUse.data?.filter(filter) || [];
  }, [filter, dataToUse]);

  const [showPendingChanges, setShowPendingChanges] = useState<boolean>(false);
  const hasPendingChanges = useMemo(() => {
    return !R.isEmpty(unlabeledEditsMap) || !R.isEmpty(labeledEditsMap);
  }, [labeledEditsMap, unlabeledEditsMap]);

  const pendingChangesCount = useMemo(() => {
    return Object.keys(dataToUse.editsMap).length;
  }, [dataToUse.editsMap]);

  const clearAllChanges = async () => {
    if (cid) {
      try {
        setDialog({
          show: true,
          title: "Do not close this window",
          body: "Changes are being discarded. Please wait a few seconds.",
          variant: AlertDialogVariants.WARNING,
        });
        await resetEdits("labeled");
        await resetEdits("unlabeled");
        setDialog({
          show: true,
          title: "Changes Discarded Successfully",
          body: "Changes have been discarded. Feel free to continue labeling or close this window.",
          variant: AlertDialogVariants.SUCCESS,
        });
      } catch (e: any) {
        setError({
          message: `Failed clearing edits from DexieDB for ${cid}. Error: ${e.message}`,
          reportError: e,
        });
      }
    }

    setShowPendingChanges(false);
  };

  const [saving, setSaving] = useState(false);
  const [incrementalSaving, setIncrementalSaving] = useState<string | null>(null);

  const sendToLambda = useCallback(
    async (rows: UpdatedRow[]) => {
      const compressedPayload = pako.gzip(JSON.stringify(rows), { to: "string" });
      const base64Payload = btoa(compressedPayload);

      if (process.env.NODE_ENV === "development") {
        console.info(
          "In development mode, posting updateManualSegmentationMaps to non-comet endpoint"
        );
        const body = {
          company: cid,
          updatedRows: base64Payload,
        };
        await SegmentationMappingLambdaFetch("/updateManualSegmentationMaps", {
          method: "POST",
          body,
        });
      } else {
        const lambdaArgs = {
          company: cid,
          updatedRows: base64Payload,
        };

        const result = await MiscLambdaFetch("/kickOffLambda", {
          method: "POST",
          body: {
            fileType: "txt",
            lambdaArgs,
            lambdaName: "segmentationmapping-updateManualSegmentationMaps",
          },
        });

        const uuid = await result.json();
        const content = await pollS3({
          autoDownload: false,
          bucket: "bpm-cache",
          filename: `${uuid}.txt`,
          mimeType: "text/plain",
          noTimeout: true,
        });
        const textContent = await content.text();
        const lambdaResult = JSON.parse(textContent);

        if (result.status !== 200) {
          throw new Error(textContent);
        }

        if (lambdaResult.cometErrorMessage) {
          throw new Error(lambdaResult.cometErrorMessage);
        }
      }
    },
    [cid]
  );

  const saveToLambda = useCallback(
    async (combinedValues: SegmentRow[]) => {
      if (!customSegments) {
        return;
      }

      const updatedRows = combinedValues?.map(edit => {
        const segmentMap: Record<string, number> = {};
        customSegments.forEach(segment => {
          if (!edit.edits) {
            return;
          }
          // Grab the id from the edits
          let updatedValueSegmentId = edit.edits[segment.segmentName]?.value;
          // If it hasn't been edited, build the segment map with the id of the original value
          if (!updatedValueSegmentId) {
            const segmentValueName = edit[segment.segmentName];
            const match = segment.values.find(val => val.valueName === segmentValueName);
            updatedValueSegmentId = match?.valueId;
          }
          segmentMap[segment.segmentId] = updatedValueSegmentId;
        });
        return {
          ad_id: edit.ad_id,
          ad_group_id: edit.ad_group_id,
          campaign_id: edit.campaign_id,
          account_id: edit.account_id,
          segmentMap,
        };
      });
      const batchRows: UpdatedRow[] = [];
      let byteSizeNum = 0;
      for (const updatedRow of updatedRows) {
        const compressedPayload = pako.gzip(JSON.stringify(updatedRow), { to: "string" });
        const base64Payload = btoa(compressedPayload);
        const byteSize = new TextEncoder().encode(base64Payload).length;
        if (byteSizeNum + byteSize > 155000 && batchRows.length > 0) {
          console.log(`Sending batch of ${batchRows.length} rows to lambda`);
          await sendToLambda(batchRows);
          batchRows.length = 0;
          byteSizeNum = 0;
        }
        batchRows.push(updatedRow);
        byteSizeNum += byteSize;
      }
      if (batchRows.length > 0) {
        console.log(`Sending batch of ${batchRows.length} rows to lambda`);
        await sendToLambda(batchRows);
      }
    },
    [sendToLambda, customSegments]
  );

  const saveIncrementally = useCallback(
    async (inputLabeledEditsMap: SegmentEditsMap) => {
      if (!customSegments) {
        return;
      }

      try {
        const combinedValues = [...Object.values(inputLabeledEditsMap ?? {})];
        await saveToLambda(combinedValues);
      } catch (e) {
        const reportError = e as Error;
        setError({ message: reportError.message, reportError });
      }
    },
    [customSegments, setError, saveToLambda]
  );

  const save = useCallback(async () => {
    if (!customSegments) {
      return;
    }

    setSaving(true);

    try {
      const combinedValues: SegmentRow[] = [
        ...Object.values<SegmentRow>(completeUnlabeledEditsMap ?? {}),
        ...Object.values<SegmentRow>(labeledEditsMap ?? {}),
      ];

      const chunks = R.splitEvery(CHUNK_SIZE, combinedValues);
      const numChunks = chunks.length;

      for (let chunkIndex = 0; chunkIndex < numChunks; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        console.log(`Saving chunk ${chunkIndex + 1} of ${numChunks}`);
        setIncrementalSaving(`Saving chunk ${chunkIndex + 1} of ${numChunks}`);
        await saveToLambda(chunk);
      }

      await fetchRawData();
      setSelectedRows({});

      // Delete the previously pending changes from Dexie
      await resetEdits("labeled");
      await removeCompleteUnlabeledEditsFromDexie();

      // Refetch the data from Dexie
      await loadDataFromDexie();

      setSaving(false);
      setIncrementalSaving(null);
    } catch (e) {
      setSaving(false);
      setIncrementalSaving(null);
      const reportError = e as Error;
      setError({ message: reportError.message, reportError });
    }
  }, [
    customSegments,
    completeUnlabeledEditsMap,
    labeledEditsMap,
    fetchRawData,
    removeCompleteUnlabeledEditsFromDexie,
    resetEdits,
    loadDataFromDexie,
    saveToLambda,
    setError,
  ]);

  const validChangesCount = useMemo(() => {
    return Object.keys(completeUnlabeledEditsMap).length + Object.keys(labeledEditsMap).length;
  }, [completeUnlabeledEditsMap, labeledEditsMap]);

  const refreshCrossChannel = useCallback(() => {
    (async () => {
      try {
        setRefreshing(true);
        Mixpanel.track(MxE.REFRESH_CC_VIEWS);
        const res = await CrossChannelLambdaFetch("/refreshCrossChannelViews", {
          method: "POST",
          body: { company: cid },
        });
        const resJson = await awaitJSON(res);
        const { message, status } = resJson;
        setRefreshing(false);
        setError({
          variant: status === "IN_PROGRESS" ? "warning" : "success",
          title: status === "IN_PROGRESS" ? "Publish already in progress" : "Publish started",
          message: <div>{message}</div>,
        });
      } catch (e: any) {
        setRefreshing(false);
        setError({
          message: `Failed to publish to dashboards for ${cid}. Error: ${e.message}`,
          reportError: e,
        });
      }
    })();
  }, [cid, setError]);

  const granularityPrettyName = useMemo(() => {
    switch (dataGranularity) {
      case "campaign":
        return "Campaign";
      case "ad_group":
        return "Ad Group";
      case "ad":
        return "Ad";
      default:
        return "";
    }
  }, [dataGranularity]);

  const [showRuleModal, setShowRuleModal] = useState<boolean>(false);

  const { customSegmentNameMap, customSegmentValueNameMap } = useMemo(() => {
    const customSegmentNameMap: Record<string, string> = {};
    const customSegmentValueNameMap: Record<string, string> = {};

    customSegments?.forEach(segment => {
      customSegmentNameMap[segment.segmentId] = segment.segmentName;
      segment.values.forEach(value => {
        customSegmentValueNameMap[value.valueId] = value.valueName;
      });
    });

    return { customSegmentNameMap, customSegmentValueNameMap };
  }, [customSegments]);

  const prepareUpdatesForRules = useCallback(
    (rowsToAlter: SegmentRow[], rules: ModifiedSegmentationMappingLabelRule[]) => {
      // Run each rule in reverse priority order
      rules.sort((a, b) => b.rulePriority - a.rulePriority);

      let updates: { rows: SegmentRow[]; edits: SegmentRowEdit }[] = [];
      rules.forEach(rule => {
        let filterTokens: Option[] = Array.isArray(rule.filterTokens) ? rule.filterTokens : [];
        if (!rule.isAdvanced) {
          filterTokens = basicToAdvanced(filterTokens, filterBarOptions);
        }
        const ruleFilter = makeFilter(filterTokens, filterBarOptions);

        const rows: SegmentRow[] = rowsToAlter?.filter(ruleFilter) || [];

        const segmentName = customSegmentNameMap[rule.setSegmentId];
        const segmentValueName = customSegmentValueNameMap[rule.setSegmentValueId];

        const update = {
          rows,
          edits: {
            [segmentName]: { label: segmentValueName, value: rule.setSegmentValueId },
          } as SegmentRowEdit,
        };
        updates.push(update);
      });
      return updates;
    },
    [customSegmentNameMap, customSegmentValueNameMap, filterBarOptions]
  );
  const applyRules = useCallback(
    async (selectedRules: ModifiedSegmentationMappingLabelRule[]) => {
      let updates: { rows: SegmentRow[]; edits: SegmentRowEdit }[] = prepareUpdatesForRules(
        Object.values(selectedRows).length > 0 ? Object.values(selectedRows) : dataToUse.data || [],
        selectedRules
      );
      setDialog({
        show: true,
        title: "Do not close this window",
        body: "Rules are still being applied. Please wait a few seconds.",
        variant: AlertDialogVariants.WARNING,
      });

      await dataToUse.updateEdits(updates);

      setDialog({
        show: true,
        title: "Rules Executed Successfully!",
        body: "Changes have been applied but are not yet saved.",
        variant: AlertDialogVariants.SUCCESS,
      });
    },
    [dataToUse, selectedRows, prepareUpdatesForRules]
  );

  const applyRulesIncrementally = useCallback(async () => {
    setSaving(true);
    const chunks = R.splitEvery(CHUNK_SIZE, dataToUse.data || []);
    const numChunks = chunks.length;
    for (let chunkIndex = 0; chunkIndex < numChunks; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      console.log(`Processing chunk ${chunkIndex + 1} of ${numChunks}`);
      setIncrementalSaving(`Processing chunk ${chunkIndex + 1} of ${numChunks}`);
      const updates = prepareUpdatesForRules(chunk, labelingRules);
      const incrementalUpdates = editsMapReducer(
        {},
        {
          type: "UPDATE_SEGMENT_EDIT",
          updates: updates,
        }
      );
      await saveIncrementally(incrementalUpdates);
    }
    setSaving(false);
    setIncrementalSaving(null);
  }, [dataToUse, labelingRules, prepareUpdatesForRules, saveIncrementally, setIncrementalSaving]);

  return (
    <div className="segmentationLabeling">
      <IncompleteUpdatesModal
        show={showIncompleteUpdatesModal}
        handleClose={() => setShowIncompleteUpdatesModal(false)}
        saveChanges={save}
        incompleteEditsMap={incompleteEditsMap}
        customSegments={customSegments}
        validChangesCount={validChangesCount}
        dataGranularity={dataGranularity}
      />
      <Nav
        activeKey={activeTab}
        onSelect={value => {
          const valueTab = value as keyof typeof value;

          if (!valueTab || valueTab === activeTab) {
            return;
          }

          setActiveTab(valueTab);
          setSelectedRows({});
        }}
      >
        <Nav.Item key={TabOptions.UNLABELED}>
          <Nav.Link eventKey={TabOptions.UNLABELED}>{TabOptions.UNLABELED}</Nav.Link>
        </Nav.Item>
        <Nav.Item key={TabOptions.LABELED}>
          <Nav.Link eventKey={TabOptions.LABELED}>{TabOptions.LABELED}</Nav.Link>
        </Nav.Item>
      </Nav>
      <div className="segmentationLabelingBody">
        {dataToUse.data ? (
          <>
            <div className="filterBarContainer">
              <FilterBar
                options={filterBarOptions}
                lines={dataToUse.data || []}
                onFilter={setFilter}
                hasAdvanced
              />
              <div className="pendingChangesControls">
                <OverlayTrigger
                  placement={OverlayTrigger.PLACEMENTS.TOP.RIGHT}
                  delay={500}
                  overlay={
                    <Tooltip>
                      {labelingRules.length > 0 &&
                        "Select a list of rules to apply to unlabeled segments. This will overwrite any existing changes currently applied to the data."}
                      {labelingRules.length === 0 &&
                        "No labeling rules available. Please create a rule on the Labeling Rules tab."}
                    </Tooltip>
                  }
                >
                  <Button
                    type={ButtonType.OUTLINED}
                    variant={ButtonFrameworkVariant.LEADING_ICON}
                    icon={<BiBookmarkHeart color={Brand60} />}
                    disabled={labelingRules.length === 0 || refreshing}
                    onClick={() => setShowRuleModal(true)}
                  >
                    {refreshing ? <Spinner /> : "Apply Labeling Rules"}
                  </Button>
                </OverlayTrigger>
                {userRole === 0 && activeTab === TabOptions.LABELED && (
                  <Button
                    type={ButtonType.OUTLINED}
                    variant={ButtonFrameworkVariant.LEADING_ICON}
                    icon={incrementalSaving ? <Spinner /> : <BiPlusMedical color={Brand60} />}
                    disabled={labelingRules.length === 0 || incrementalSaving !== null}
                    onClick={() => applyRulesIncrementally()}
                  >
                    {incrementalSaving ? incrementalSaving : "Apply Rules Incrementally"}
                  </Button>
                )}
                <SelectLabelingRulesModal
                  show={showRuleModal}
                  setShow={setShowRuleModal}
                  labelingRules={labelingRules}
                  customSegments={customSegments}
                  onRuleApply={applyRules}
                />
                <OverlayTrigger
                  placement={OverlayTrigger.PLACEMENTS.TOP.RIGHT}
                  delay={500}
                  overlay={
                    <Tooltip>
                      {hasPendingChanges
                        ? "Please save any labeling changes before publishing to other parts of the app."
                        : "Publish the latest mapping changes to dashboards now.\nChanges will auto-update by the next day if not manually published."}
                    </Tooltip>
                  }
                >
                  <Button
                    type={ButtonType.OUTLINED}
                    variant={ButtonFrameworkVariant.LEADING_ICON}
                    icon={<MdSend color={Brand60} />}
                    onClick={() => refreshCrossChannel()}
                    disabled={hasPendingChanges}
                  >
                    {refreshing ? <Spinner /> : "Publish to dashboards"}
                  </Button>
                </OverlayTrigger>
                <Button
                  type={ButtonType.FILLED}
                  variant={ButtonFrameworkVariant.ICON_ONLY}
                  icon={<MdOutlineFileDownload />}
                  onClick={() => {
                    const { data, editsMap } = dataToUse;
                    const date = new Date();
                    const csvData = R.map(row => editsMap[row.campaign_key] ?? row, data || []).map(
                      row => {
                        const cleanedRow: any = {
                          ...R.mapObjIndexed(v => (v === null ? "" : v), row),
                          segmentation_id: row.ingested_seg_id ?? row.pg_seg_id,
                        };
                        delete cleanedRow.ingested_seg_id;
                        delete cleanedRow.pg_seg_id;
                        delete cleanedRow.platform_id;
                        delete cleanedRow.channel_id;
                        return cleanedRow;
                      }
                    );

                    downloadJSONToCSV(
                      csvData,
                      `SegmentationLabeling ${cid} ${activeTab.toLowerCase()} ${date.toUTCString()}`
                    );
                  }}
                />
              </div>
            </div>
            <SegmentationLabelingTable
              data={filteredData}
              customSegments={customSegments}
              editsMap={dataToUse.editsMap}
              selectedRows={selectedRows}
              dispatchEditsMap={action => {
                if (action.type === "UPDATE_SEGMENT_EDIT") {
                  dataToUse.updateEdits(action.updates || []);
                } else if (action.type === "DELETE_SEGMENT_EDIT") {
                  deleteSegmentEdit(
                    action.campaign_keys || [],
                    action.segmentName || "",
                    activeTab === TabOptions.LABELED ? "labeled" : "unlabeled"
                  );
                } else if (action.type === "RESET") {
                  resetEdits(activeTab === TabOptions.LABELED ? "labeled" : "unlabeled");
                }
              }}
              setSelectedRows={setSelectedRows}
              dataGranularity={dataGranularity}
            />
          </>
        ) : (
          <FullPageSpinner />
        )}
        <div className="segmentationLabelingControls">
          <div className="pendingChangesControls">
            <div className="unsavedChanges">
              <span className="unlabeledCounter">
                <span className={cn({ unsavedCount: hasPendingChanges })}>
                  {formatNumber(labeledData?.length ?? 0 + pendingChangesCount)}
                </span>
                /{formatNumber((unlabeledData?.length ?? 0) + (labeledData?.length ?? 0))}{" "}
                {granularityPrettyName}s Labeled
              </span>
              {hasPendingChanges && (
                <Button
                  type={ButtonType.OUTLINED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  design="secondary"
                  className="danger"
                  icon={<MdErrorOutline />}
                  onClick={() => {
                    setShowPendingChanges(true);
                  }}
                >
                  See {pendingChangesCount} Unsaved Change{pendingChangesCount > 1 ? "s" : ""}
                </Button>
              )}
            </div>
            {hasPendingChanges && (
              <>
                <Button
                  type={ButtonType.OUTLINED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  onClick={clearAllChanges}
                  disabled={!hasPendingChanges}
                  icon={<MdDelete color={Brand60} />}
                >
                  Discard Changes
                </Button>
                <Button
                  type={ButtonType.FILLED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  onClick={
                    Object.keys(incompleteEditsMap).length > 0
                      ? () => {
                          setShowIncompleteUpdatesModal(true);
                        }
                      : save
                  }
                  disabled={saving || !hasPendingChanges}
                  icon={incrementalSaving ? <Spinner /> : <MdSave />}
                >
                  {saving ? incrementalSaving : "Save"}
                </Button>
              </>
            )}
          </div>
        </div>
        {showPendingChanges && hasPendingChanges && (
          <PendingSegmentationChanges
            incompleteUnlabeledEditsMap={incompleteEditsMap}
            completeUnlabeledEditsMap={completeUnlabeledEditsMap}
            labeledEditsMap={labeledEditsMap}
            dispatchUnlabeledEditsMap={action => {
              if (action.type === "UPDATE_SEGMENT_EDIT") {
                updateUnlabeledEdits(action.updates || []);
              } else if (action.type === "DELETE_SEGMENT_EDIT") {
                deleteSegmentEdit(
                  action.campaign_keys || [],
                  action.segmentName || "",
                  "unlabeled"
                );
              } else if (action.type === "RESET") {
                resetEdits("unlabeled");
              }
            }}
            dispatchLabeledEditsMap={action => {
              if (action.type === "UPDATE_SEGMENT_EDIT") {
                updateLabeledEdits(action.updates || []);
              } else if (action.type === "DELETE_SEGMENT_EDIT") {
                deleteSegmentEdit(action.campaign_keys || [], action.segmentName || "", "labeled");
              } else if (action.type === "RESET") {
                resetEdits("labeled");
              }
            }}
            setShowPendingChanges={setShowPendingChanges}
            customSegments={customSegments}
            dataGranularity={dataGranularity}
          />
        )}
      </div>
      {dialog && (
        <AlertDialog
          show={dialog.show}
          onHide={() => setDialog(null)}
          icon={null}
          title={dialog.title}
          body={dialog.body}
          primaryButtonLabel="Close"
          variant={dialog.variant}
        />
      )}
    </div>
  );
};

export default SegmentationLabeling;
